/** Per-account character sheet layout: which sections are hidden, collapsed, and their order (stored on the server). */
import { useEffect, useRef, useState } from "react";
import type { SheetPrefs } from "@dm/shared";
import { api } from "./api.js";

export interface SectionDef {
  id: string;
  title: string;
  /** Spans both columns on wide screens. */
  wide?: boolean;
}

export const SECTIONS: SectionDef[] = [
  { id: "header", title: "Basics", wide: true },
  { id: "combat", title: "Combat" },
  { id: "conditions", title: "Conditions" },
  { id: "abilities", title: "Abilities & saves" },
  { id: "skills", title: "Skills" },
  { id: "attacks", title: "Attacks & cantrips" },
  { id: "proficiencies", title: "Proficiencies & languages" },
  { id: "spells", title: "Spellcasting", wide: true },
  { id: "features", title: "Features & traits" },
  { id: "equipment", title: "Equipment & money", wide: true },
  { id: "personality", title: "Personality & backstory" },
  { id: "notes", title: "Notes" },
];
const IDS = SECTIONS.map((s) => s.id);

export const defaultPrefs = (): SheetPrefs => ({ hidden: [], collapsed: ["personality"], order: [...IDS] });

export function normalizePrefs(p: Partial<SheetPrefs> | undefined): SheetPrefs {
  if (!p) return defaultPrefs();
  const known = (v: unknown): string[] => (Array.isArray(v) ? [...new Set(v.filter((x): x is string => IDS.includes(x as string)))] : []);
  const order = known(p.order);
  for (const id of IDS) if (!order.includes(id)) order.splice(Math.min(IDS.indexOf(id), order.length), 0, id);
  return { hidden: known(p.hidden), collapsed: known(p.collapsed), order };
}

let cache: SheetPrefs | null = null;

/** Loads this account's sheet prefs once, and saves changes (debounced) back to the server. */
export function useSheetPrefs() {
  const [prefs, setPrefs] = useState<SheetPrefs>(() => cache ?? defaultPrefs());
  const [loaded, setLoaded] = useState(cache !== null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (cache) return;
    let gone = false;
    api.preferences().then(
      (r) => {
        cache = normalizePrefs(r.preferences.sheet);
        if (!gone) {
          setPrefs(cache);
          setLoaded(true);
        }
      },
      () => !gone && setLoaded(true),
    );
    return () => {
      gone = true;
    };
  }, []);

  const update = (fn: (p: SheetPrefs) => SheetPrefs) => {
    setPrefs((cur) => {
      const next = normalizePrefs(fn(cur));
      cache = next;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void api.setSheetPrefs(next).catch(() => undefined), 400);
      return next;
    });
  };

  const toggleIn = (key: "hidden" | "collapsed", id: string) =>
    update((p) => ({ ...p, [key]: p[key].includes(id) ? p[key].filter((x) => x !== id) : [...p[key], id] }));

  return {
    prefs,
    loaded,
    toggleHidden: (id: string) => toggleIn("hidden", id),
    toggleCollapsed: (id: string) => toggleIn("collapsed", id),
    setAllCollapsed: (collapsed: boolean) => update((p) => ({ ...p, collapsed: collapsed ? [...IDS] : [] })),
    move: (id: string, dir: -1 | 1) =>
      update((p) => {
        const order = [...p.order];
        const i = order.indexOf(id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= order.length) return p;
        [order[i], order[j]] = [order[j]!, order[i]!];
        return { ...p, order };
      }),
    reset: () => update(() => defaultPrefs()),
  };
}
